/**
 * Allys Contact Map Hero Component
 * 
 * Hero section with an embedded map showing the office location
 */

'use client';

import { memo } from 'react';

// Helper to safely get string values from messages
const safeGetString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  return fallback;
};

// Helper to safely get object values
const safeGetObject = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  return {};
};

export interface AllysContactMapHeroProps {
  messages?: Record<string, unknown>;
  locale?: string;
}

export const AllysContactMapHero = memo(function AllysContactMapHero({
  messages,
  locale: _locale
}: AllysContactMapHeroProps) {
  const contactMessages = messages?.contact as Record<string, unknown> | undefined;
  const heroMessages = safeGetObject(contactMessages?.hero);

  // Coordinates for Beau Bassin-Rose Hill, Mauritius
  const latitude = -20.2458;
  const longitude = 57.4701;

  // OpenStreetMap iframe embed URL
  const mapEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.05},${latitude - 0.05},${longitude + 0.05},${latitude + 0.05}&layer=mapnik&marker=${latitude},${longitude}`;

  return (
    <div className="relative">
      {/* Map Background with Overlay */}
      <div className="relative h-[500px] lg:h-[600px] overflow-hidden bg-gray-200">
        {/* Embedded Map */}
        <iframe
          title="Office Location Map"
          src={mapEmbedUrl}
          className="absolute inset-0 w-full h-full border-0"
          style={{
            filter: 'grayscale(100%) contrast(1.1) brightness(0.9)',
            pointerEvents: 'none',
          }}
          loading="lazy"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
        
        {/* Content Overlay */}
        <div className="relative h-full flex items-center justify-center z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight font-inter"
            >
              {safeGetString(heroMessages?.title, 'Get In Touch')}
            </h1>
            <p 
              className="text-xl lg:text-2xl text-white/90 max-w-2xl mx-auto font-inter"
            >
              {safeGetString(heroMessages?.subtitle, 'Let us help you find your dream property in Mauritius')}
            </p>
            
            {/* Location Badge */}
            <div className="mt-8 inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-white font-medium font-inter">
                Beau Bassin-Rose Hill, Mauritius
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
    </div>
  );
});

